import { CompanyAPI } from '@/api/backend-client'
import { UserAPI } from '@/api/backend-client'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'
import { UserType } from '@/api/types'

export const getAllowedUsers = async (
    referredByBranch: Branch|null,
    referredByUser: User|null,
    assignedToBranch: Branch|null,
    assignedToUser: User|null,
): Promise<User[]> => {
    const users = new Map<string, User>()

    if (referredByBranch) {
        const referredByCompany = await CompanyAPI.get(referredByBranch.company.id)
        referredByCompany?.users.forEach((user: User) => users.set(user.id, user))
    }

    if (referredByUser) {
        referredByUser = await UserAPI.get(referredByUser.id)
        users.set(referredByUser.id, referredByUser)
    }

    if (assignedToBranch) {
        const assignedToCompany = await CompanyAPI.get(assignedToBranch.company.id)
        assignedToCompany.users.forEach((user: User) => users.set(user.id, user))
    }

    if (assignedToUser) {
        assignedToUser = await UserAPI.get(assignedToUser.id)
        users.set(assignedToUser.id, assignedToUser)
    }

    return Array.from(users.values())
        .filter((user: User) => {
            if (user.type !== UserType.DEFAULT) {
                return false
            }

            return isAllowedUser(user, referredByBranch, referredByUser, assignedToBranch, assignedToUser)
        })

        .sort((a: User, b: User) => a.name.localeCompare(b.name))
}

export const isAllowedUser = (
    user: User,
    referredByBranch: Branch|null,
    referredByUser: User|null,
    assignedToBranch: Branch|null,
    assignedToUser: User|null,
): boolean => {
    if (user.isCompanyManager) {
        // For company managers, the user must be in the referred by or assigned to company
        if (referredByBranch && referredByBranch.company.id === user.company.id) {
            return true
        }

        if (assignedToBranch && assignedToBranch.company.id === user.company.id) {
            return true
        }

        return false
    } else if (user.isBranchManager || user.isTeamLead) {
        // For branch managers and team leads, the user must be in the referred by or assigned to branch
        if (referredByBranch && user.branches.some((branch: Branch) => referredByBranch.id === branch.id)) {
            return true
        }

        if (assignedToBranch && user.branches.some((branch: Branch) => assignedToBranch.id === branch.id)) {
            return true
        }

        return false
    } else {
        // For employees, the user must be the referred by or assigned to user
        if (referredByUser && referredByUser.id === user.id) {
            return true
        }

        if (assignedToUser && assignedToUser.id === user.id) {
            return true
        }

        return false
    }
}
